import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Search, FileText, CalendarDays, Users2, Download, Calendar, X } from 'lucide-react';
import { adminAPI } from '../services/adminApi';
import './ActivityLog.css';

const PAGE_SIZE = 10;

function getPageNumbers(current, total) {
    const delta = 1;
    const pages = [];
    for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
            pages.push(i);
        }
    }
    const withDots = [];
    let prev;
    for (const p of pages) {
        if (prev !== undefined) {
            if (p - prev === 2) withDots.push(prev + 1);
            else if (p - prev > 2) withDots.push('…');
        }
        withDots.push(p);
        prev = p;
    }
    return withDots;
}

function formatTime(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDayLabel(dayKey) {
    const [, m, d] = dayKey.split('-');
    return `${d}-${MONTHS[parseInt(m, 10) - 1]}`;
}

const ActivityLog = () => {
    const [rows, setRows] = useState([]);
    const [dayKeys, setDayKeys] = useState([]);
    const [cards, setCards] = useState({});
    const [dailyTarget, setDailyTarget] = useState(40);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');

    
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const dateRangeRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (dateRangeRef.current && !dateRangeRef.current.contains(e.target)) setDateRangeOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const fetchPage = useCallback((p) => {
        setLoading(true);
        const params = { page: p, limit: PAGE_SIZE };
        if (debounced) params.search = debounced;
        if (dateFrom && dateTo) {
            params.date_from = dateFrom;
            params.date_to = dateTo;
        }

        adminAPI.getActivityLog(params)
            .then(r => {
                setRows(r.data?.data || []);
                setDayKeys(r.data?.dayKeys || []);
                setPagination(r.data?.pagination || {});
                setCards(r.data?.cards || {});
                setDailyTarget(r.data?.dailyTarget || 40);
                setPage(p);
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [debounced, dateFrom, dateTo]);

    useEffect(() => { fetchPage(1); }, [fetchPage]);

    const hasDateRange = Boolean(dateFrom && dateTo);
    const colCount = hasDateRange ? (7 + dayKeys.length) : 7;

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = {};
            if (debounced) params.search = debounced;
            if (hasDateRange) {
                params.date_from = dateFrom;
                params.date_to = dateTo;
            }
            const res = await adminAPI.getActivityLogExport(params);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            const suffix = hasDateRange ? `${dateFrom}_to_${dateTo}` : 'summary';
            a.download = `activity_log_${suffix}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            // optionally surface a toast/error here
        } finally {
            setExporting(false);
        }
    };

    const clearDateRange = () => { setDateFrom(''); setDateTo(''); };

    const totalPages = pagination.totalPages || 1;
    const total = pagination.total || 0;

    return (
        <div className="admin-activity-page">
            <h1 className="admin-page-title">User Activity Log</h1>

            <div className="admin-stat-cards">
                <div className="admin-stat-card">
                    <div className="admin-stat-icon tone-teal"><FileText size={20} /></div>
                    <div>
                        <div className="admin-stat-value">{loading ? '—' : (cards.totalReports ?? 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Total Reports</div>
                    </div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon tone-amber"><CalendarDays size={20} /></div>
                    <div>
                        <div className="admin-stat-value">{loading ? '—' : (cards.todayReports ?? 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Today's Reports</div>
                    </div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon tone-indigo"><Users2 size={20} /></div>
                    <div>
                        <div className="admin-stat-value">{loading ? '—' : (cards.totalUsers ?? 0).toLocaleString()}</div>
                        <div className="admin-stat-label">Users</div>
                    </div>
                </div>
            </div>

            <div className="admin-reports-toolbar">
                <div className="admin-search-wrap">
                    <Search size={15} className="admin-search-icon" />
                    <input
                        className="admin-search-input"
                        placeholder="Search by user name..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <div className="admin-daterange-wrap" ref={dateRangeRef}>
                    <button
                        type="button"
                        className="admin-daterange-trigger"
                        onClick={() => setDateRangeOpen(o => !o)}
                    >
                        <span className={hasDateRange ? '' : 'admin-daterange-placeholder'}>
                            {hasDateRange ? `${dateFrom} - ${dateTo}` : 'Select'}
                        </span>
                        <Calendar size={14} />
                    </button>

                    {dateRangeOpen && (
                        <div className="admin-daterange-popover">
                            <div className="admin-daterange-field">
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    value={dateFrom}
                                    max={dateTo || undefined}
                                    onChange={e => setDateFrom(e.target.value)}
                                />
                            </div>
                            <div className="admin-daterange-field">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    value={dateTo}
                                    min={dateFrom || undefined}
                                    onChange={e => setDateTo(e.target.value)}
                                />
                            </div>
                            {hasDateRange && (
                                <button type="button" className="admin-daterange-clear" onClick={clearDateRange}>
                                    <X size={13} /> Clear
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <button className="admin-export-btn" onClick={handleExport} disabled={exporting}>
                    <Download size={14} /> {exporting ? 'Exporting…' : 'Export'}
                </button>
            </div>

            <div className="admin-table-card">
                <div className="admin-table-scroll">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Login Time</th>
                            <th>Logout Time</th>
                            {hasDateRange && dayKeys.map(d => <th key={d}>{formatDayLabel(d)}</th>)}
                            <th>Today</th>
                            <th>This Week</th>
                            <th>This Month</th>
                            <th>Total Reports</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={colCount} className="admin-table-empty">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={colCount} className="admin-table-empty">No users found.</td></tr>
                        ) : (
                            rows.map(u => {
                                return (
                                    <tr key={u.id}>
                                        <td>
                                            <div>{u.name}</div>
                                            {u.email && <div className="admin-created-time">{u.email}</div>}
                                        </td>
                                        <td>
                                            <span className="admin-login-time">{formatTime(u.productivity?.loginTime)}</span>
                                        </td>
                                        <td>
                                            {u.productivity?.active
                                                ? <span className="admin-logout-time admin-status-active">Active</span>
                                                : <span className="admin-logout-time">{formatTime(u.productivity?.logoutTime)}</span>
                                            }
                                        </td>
                                        {hasDateRange && dayKeys.map(d => (
                                            <td key={d}>{u.days?.[d] ?? 0}</td>
                                        ))}
                                        <td>{u.today}</td>
                                        <td>{u.week}</td>
                                        <td>{u.month}</td>
                                        <td>{u.total}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
                </div>

                <div className="pagination">
                    <span className="pg-info">
                        Showing {total > 0 ? (page - 1) * PAGE_SIZE + 1 : 0} to {Math.min(page * PAGE_SIZE, total)} of {total} entries
                    </span>
                    <div className="pg-controls">
                        <button className="pg-btn pg-btn-word" onClick={() => fetchPage(page - 1)} disabled={page === 1}>Previous</button>
                        {getPageNumbers(page, totalPages).map((p, idx) => (
                            p === '…'
                                ? <span key={`dots-${idx}`} className="pg-dots">…</span>
                                : <button key={p} className={`pg-btn ${page === p ? 'active' : ''}`} onClick={() => fetchPage(p)}>{p}</button>
                        ))}
                        <button className="pg-btn pg-btn-word" onClick={() => fetchPage(page + 1)} disabled={page === totalPages}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActivityLog;